import React, { useState, useEffect, useCallback } from 'react';
import './index.css';
import { supabase } from './lib/supabase';
import { useAuth } from './lib/auth';
import { useTranslation } from 'react-i18next';
import { useLiveData } from './lib/liveData';
import { runFullModel } from './lib/scoring';
import Nav from './components/Nav';
import Toast from './components/Toast';
import Data from './pages/Data';
import Regional from './pages/Regional';
import History from './pages/History';
import Settings from './pages/Settings';
import ModelLogic from './pages/ModelLogic';
import Users from './pages/Users';
import Oracle from './pages/Oracle';
import Performance from './pages/Performance';

// ── DEFAULT INPUTS (pre-filled with latest known data) ───────────
const DEFAULT_INPUTS = {
  // Oil
  wti_price: 101.94,
  brent_price: 107,

  // Hormuz
  hormuz_traffic_pct: 5,
  hormuz_status: 'closed',
  vlcc_rate_premium: 120,

  // Geopolitical
  conflict_weeks: 10,
  recent_events: ['vp_level_talks'],
  polymarket_peace: null,
  geopolitical_event: 'Vance–Iran peace talks in Pakistan. US escort convoy operational. Iran: ceasefire violation.',

  // China PP
  pp_spot_qingdao: 10000,
  pp_spot_zhenhai: 10100,
  pp_spot_jiujiang: 9200,
  pp_spot_daqing: 9070,
  pp_spot_fushun: 9050,
  dce_futures_m1: 9200,
  dce_futures_m3: 9000,
  propylene_spot: 8500,
  naphtha_crack: 85,
  plant_op_rate: 71,
  china_export_cushion: true,

  // Market stress
  import_landed_cost: 10800,
  supplier_allocation: 'open',
  bopp_op_rate: 65,

  // SEA
  sea_bopp_cfr_low: 1210,
  sea_bopp_cfr_high: 1370,
  sea_film_cfr_low: 1230,
  sea_film_cfr_high: 1370,

  // KAPS (internal) — null = not yet entered; zero is a valid value (plant offline)
  kaps_stock_mt: null,
  kaps_stock_bonded_mt: 0,
  kaps_transit_mt: null,
  kaps_pr_mt: null,
  kaps_avg_cost: null,
  kaps_safety_stock_mt: null,
  kaps_daily_demand_mt: null,

  // KICP (internal) — null = not yet entered
  kicp_gloss18_sell_price: null,
  kicp_portfolio_avg_sell: null,
  kicp_gloss18_qty_mt: 0,
  kicp_portfolio_qty_mt: null,
  kicp_portfolio_qty_prev_mt: null,
  kicp_portfolio_avg_sell_prev: null,
  net_revision_pct: 0,

  // Scenario band offsets (fraction of spot — e.g. 0.92 = 92% of spot)
  scenario_peace_low: 0.92,      scenario_peace_mid: 0.97,      scenario_peace_high: 1.02,
  scenario_stalemate_low: 1.00,  scenario_stalemate_mid: 1.05,  scenario_stalemate_high: 1.10,
  scenario_escalation_low: 1.10, scenario_escalation_mid: 1.20, scenario_escalation_high: 1.30,
  scenario_strike_low: 1.30,     scenario_strike_mid: 1.50,     scenario_strike_high: 1.70,

  // Config
  conversion_cost: 1800,
  lead_time_days: 35,
  pp_yield_ratio: 1.03,
  currency_rate: 7.25,
};

const ALL_TABS = [
  { id: 'oracle',     label: 'ORACLE',        icon: '🔮', minRole: 'viewer' },
  { id: 'regional',   label: 'SEA Regional',  icon: '🌏', minRole: 'viewer' },
  { id: 'data',        label: 'Data',          icon: '📊', minRole: 'viewer' },
  { id: 'history',    label: 'History',       icon: '📈', minRole: 'viewer' },
  { id: 'settings',   label: 'Settings',      icon: '⚙️', minRole: 'data_updater' },
  { id: 'users',       label: 'Users',         icon: '👥', minRole: 'admin' },
  { id: 'performance', label: 'VERITAS',       icon: '📐', minRole: 'admin' },
  { id: 'logic',       label: 'Model Logic',   icon: '🧮', minRole: 'viewer' },
];

function getTabsForRole(role) {
  const ranks = { viewer: 1, data_updater: 2, admin: 3 };
  const userRank = ranks[role] || 1;
  return ALL_TABS.filter(t => userRank >= ranks[t.minRole]);
}

function computeAdjustments(signal) {
  if (!signal) return {};
  const hormuzMap = { 1: 0, 2: 3, 3: 8, 4: 15, 5: 20 };
  const opecMap   = { '-3': 10, '-2': 6, '-1': 3, '0': 0, '1': -3, '2': -6, '3': -10 };
  const stimMap   = { '-3': -8, '-2': -5, '-1': -2, '0': 0, '1': 2, '2': 5, '3': 8 };
  const petroMap  = { '-3': -6, '-2': -4, '-1': -2, '0': 0, '1': 2, '2': 4, '3': 6 };
  const clampD = (v, lo, hi) => Math.min(hi, Math.max(lo, v));
  const h = hormuzMap[signal.hormuz_tension] ?? 0;
  const o = opecMap[String(signal.opec_supply)] ?? 0;
  const s = stimMap[String(signal.china_stimulus)] ?? 0;
  const p = petroMap[String(signal.petrochem_sentiment)] ?? 0;
  return {
    stiDelta: clampD(h + o, -20, 20),
    msiDelta: clampD(h + o, -20, 20),
    dpiDelta: clampD(s + p, -12, 12),
  };
}

export default function App() {
  const { user, canSave, role } = useAuth();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('oracle');
  const [inputs, setInputs] = useState(() => {
    const saved = localStorage.getItem('pp_map_inputs');
    return saved ? { ...DEFAULT_INPUTS, ...JSON.parse(saved) } : DEFAULT_INPUTS;
  });
  const [results, setResults] = useState(null);
  const [toasts, setToasts] = useState([]);
  const [settings, setSettings] = useState(() => {
    const saved = localStorage.getItem('pp_map_settings');
    return saved ? JSON.parse(saved) : {
      kaps_api_url: '',
      kaps_api_key: '',
      kaps_enabled: false,
      kicp_api_url: '',
      kicp_api_token: '',
      kicp_enabled: false,
      currency_rate: 7.25,
      default_conversion_cost: 1800,
      company_name: 'Kunlene BOPP Operations',
      sti_alert_threshold: 75,
      msi_alert_threshold: 70,
      safety_breach_alert_days: 10,
    };
  });
  const [isSaving, setIsSaving] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [apiStatus, setApiStatus] = useState({ kaps: 'unchecked', kicp: 'unchecked' });
  const [aiSignal, setAiSignal] = useState(null);

  // ── Live data feeds (WTI, Brent, CNY, Polymarket) ──
  const liveData = useLiveData((liveValues) => {
    // When new live values arrive, merge them into inputs
    setInputs(prev => ({ ...prev, ...liveValues }));
  });

  // ── Fetch latest AI market signal (daily, on mount only) ─────
  useEffect(() => {
    async function loadSignal() {
      const { data, error } = await supabase
        .from('daily_market_signals')
        .select('*')
        .order('signal_date', { ascending: false })
        .limit(1)
        .single();
      if (!error && data) setAiSignal(data);
    }
    loadSignal();
  }, []);

  // Tab access guard — if user's role doesn't allow current tab, send them to dashboard
  useEffect(() => {
    const allowedTabs = getTabsForRole(role).map(t => t.id);
    if (!allowedTabs.includes(activeTab)) setActiveTab('oracle');
  }, [role, activeTab]);

  // ── Run model whenever inputs change ──────────────────────────
  useEffect(() => {
    if (inputs.kaps_stock_mt == null || inputs.kaps_daily_demand_mt == null) {
      setResults({
        status: 'incomplete',
        missingInputs: [
          ...(inputs.kaps_stock_mt == null ? ['kaps_stock_mt'] : []),
          ...(inputs.kaps_daily_demand_mt == null ? ['kaps_daily_demand_mt'] : []),
        ],
        computedAt: new Date().toISOString(),
      });
      return;
    }
    try {
      const r = runFullModel(inputs, computeAdjustments(aiSignal));
      setResults(r);
    } catch (e) {
      console.error('Model error:', e);
    }
  }, [inputs, aiSignal]);

  // ── Auto-save inputs to localStorage ─────────────────────────
  useEffect(() => {
    localStorage.setItem('pp_map_inputs', JSON.stringify(inputs));
  }, [inputs]);

  // ── Update a single input ─────────────────────────────────────
  const updateInput = useCallback((key, value) => {
    setInputs(prev => ({ ...prev, [key]: value }));
  }, []);

  const updateInputs = useCallback((updates) => {
    setInputs(prev => ({ ...prev, ...updates }));
  }, []);

  // ── Save run to Supabase ──────────────────────────────────────
  const saveRun = useCallback(async () => {
    if (!results) return;
    if (results?.status === 'incomplete') {
      addToast(t('common.save_run_blocked_tooltip'), 'error');
      return;
    }
    if (!canSave) {
      addToast(t('save.no_permission'), 'error');
      return;
    }
    setIsSaving(true);
    try {
      const runData = {
        run_date: new Date().toISOString().split('T')[0],
        user_email: user?.email || null,
        ...inputs,
        sti_score: results.sti.total,
        msi_score: results.msi.total,
        dpi_score: results.dpi.total,
        oti_score: results.oti.total,
        peace_probability: results.scenarios.probabilities.peace / 100,
        stalemate_probability: results.scenarios.probabilities.stalemate / 100,
        escalation_probability: results.scenarios.probabilities.escalation / 100,
        strike_probability: results.scenarios.probabilities.strike / 100,
        pp_expected_7d: results.scenarios.pp_expected_7d,
        pp_p10_7d: results.scenarios.pp_p10_7d,
        pp_p90_7d: results.scenarios.pp_p90_7d,
        gloss18_margin: results.margins.gloss18,
        portfolio_margin: results.margins.portfolio,
        recommendation: results.recommendation.action,
        recommendation_rationale: results.recommendation.rationaleKey
          ? t(results.recommendation.rationaleKey, { lng: 'en', ...(results.recommendation.rationaleParams || {}) })
          : (results.recommendation.rationale || ''),
      };
      const { error } = await supabase.from('pp_map_runs').insert([runData]);
      if (error) throw error;
      setLastSaved(new Date());
      addToast('Run saved to database', 'success');
    } catch (e) {
      console.error('Save error:', e);
      addToast('Save failed — check Supabase connection', 'error');
    } finally {
      setIsSaving(false);
    }
  }, [inputs, results]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Save VERITAS prediction run ───────────────────────────────
  const saveManualRun = useCallback(async () => {
    if (!results) return;
    if (results?.status === 'incomplete') {
      addToast(t('common.save_run_blocked_tooltip'), 'error');
      return;
    }
    if (!canSave) {
      addToast(t('save.no_permission'), 'error');
      return;
    }
    const { scenarios, recommendation, sti, msi, safety } = results;
    try {
      const { computeVolumeSizing } = await import('./lib/oracleSizing');
      const sizing = computeVolumeSizing({
        action:            recommendation.action,
        safetyUrgency:     safety.urgency,
        ppSpot:            inputs.pp_spot_qingdao,
        scenarios,
        kapsStockMT:       inputs.kaps_stock_mt,
        kapsTransitMT:     inputs.kaps_transit_mt,
        kapsDailyDemandMT: inputs.kaps_daily_demand_mt,
      });
      const { error } = await supabase.from('predictions').insert([{
        run_date:        new Date().toISOString().split('T')[0],
        source:          'manual_save',
        pp_spot_qingdao: inputs.pp_spot_qingdao,
        ev_7d:           scenarios.pp_expected_7d,
        p10_7d:          scenarios.pp_p10_7d,
        p90_7d:          scenarios.pp_p90_7d,
        action:          recommendation.action,
        sizing_volume_mt: sizing.volumeMT,
        sti_total:       sti.total,
        msi_total:       msi.total,
        peace_pct:       scenarios.probabilities.peace,
        stalemate_pct:   scenarios.probabilities.stalemate,
        escalation_pct:  scenarios.probabilities.escalation,
        strike_pct:      scenarios.probabilities.strike,
        saved_by:        user?.id || null,
      }]);
      if (error) throw error;
      addToast(t('veritas.save_run_success'), 'success');
    } catch (e) {
      console.error('VERITAS save error:', e);
      addToast(t('veritas.save_run_fail'), 'error');
    }
  }, [inputs, results, canSave]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Fetch live data from KAPS/KICP ───────────────────────────
  const fetchKAPSData = useCallback(async () => {
    if (!settings.kaps_enabled || !settings.kaps_api_url) {
      addToast('KAPS API not configured. Go to Settings.', 'error');
      return;
    }
    try {
      setApiStatus(s => ({ ...s, kaps: 'loading' }));
      const res = await fetch(`${settings.kaps_api_url}/api/pp/stock-summary`, {
        headers: { 'Authorization': `Bearer ${settings.kaps_api_key}`, 'Content-Type': 'application/json' }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      // Map KAPS response to our inputs
      updateInputs({
        kaps_stock_mt: data.realtime_stock_mt || inputs.kaps_stock_mt,
        kaps_transit_mt: data.in_transit_mt || inputs.kaps_transit_mt,
        kaps_pr_mt: data.pr_qty_mt || inputs.kaps_pr_mt,
        kaps_avg_cost: data.avg_cost_rmb || inputs.kaps_avg_cost,
        kaps_safety_stock_mt: data.safety_stock_mt || inputs.kaps_safety_stock_mt,
        kaps_daily_demand_mt: data.daily_demand_mt || inputs.kaps_daily_demand_mt,
      });
      setApiStatus(s => ({ ...s, kaps: 'ok' }));
      addToast('KAPS data refreshed', 'success');
    } catch (e) {
      console.error('KAPS fetch error:', e);
      setApiStatus(s => ({ ...s, kaps: 'error' }));
      addToast(`KAPS fetch failed: ${e.message}`, 'error');
    }
  }, [settings, inputs, updateInputs]);

  const fetchKICPData = useCallback(async () => {
    if (!settings.kicp_enabled || !settings.kicp_api_url) {
      addToast('KICP API not configured. Go to Settings.', 'error');
      return;
    }
    try {
      setApiStatus(s => ({ ...s, kicp: 'loading' }));
      // Fetch all film orders
      const res = await fetch(`${settings.kicp_api_url}/api/film/orders-summary?film_type=all`, {
        headers: { 'Authorization': `Bearer ${settings.kicp_api_token}`, 'Content-Type': 'application/json' }
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      // Also fetch Gloss 18µm specifically
      const res2 = await fetch(`${settings.kicp_api_url}/api/film/orders-summary?film_type=GLOSS_18UM`, {
        headers: { 'Authorization': `Bearer ${settings.kicp_api_token}`, 'Content-Type': 'application/json' }
      });
      const data2 = res2.ok ? await res2.json() : {};
      updateInputs({
        kicp_portfolio_avg_sell: data.weighted_avg_sell_price_rmb || inputs.kicp_portfolio_avg_sell,
        kicp_portfolio_qty_mt: (data.confirmed_qty_kg || 0) / 1000 || inputs.kicp_portfolio_qty_mt,
        kicp_gloss18_sell_price: data2.avg_sell_price_rmb || inputs.kicp_gloss18_sell_price,
        kicp_gloss18_qty_mt: (data2.confirmed_qty_kg || 0) / 1000 || inputs.kicp_gloss18_qty_mt,
      });
      setApiStatus(s => ({ ...s, kicp: 'ok' }));
      addToast('KICP data refreshed', 'success');
    } catch (e) {
      console.error('KICP fetch error:', e);
      setApiStatus(s => ({ ...s, kicp: 'error' }));
      addToast(`KICP fetch failed: ${e.message}`, 'error');
    }
  }, [settings, inputs, updateInputs]);

  // ── Toast manager ─────────────────────────────────────────────
  const addToast = (message, type = 'default') => {
    const id = Date.now();
    setToasts(prev => [...prev, { id, message, type }]);
    setTimeout(() => setToasts(prev => prev.filter(t => t.id !== id)), 3000);
  };

  // ── Settings save ─────────────────────────────────────────────
  const saveSettings = useCallback((newSettings) => {
    setSettings(newSettings);
    localStorage.setItem('pp_map_settings', JSON.stringify(newSettings));
    addToast('Settings saved', 'success');
  }, []);

  const sharedProps = {
    inputs, updateInput, updateInputs, results, settings,
    fetchKAPSData, fetchKICPData, apiStatus, addToast,
    liveData, saveManualRun, setActiveTab, aiSignal,
  };

  return (
    <div className="app-layout">
      <Nav
        tabs={getTabsForRole(role)}
        activeTab={activeTab}
        setActiveTab={setActiveTab}
        onSave={saveRun}
        isSaving={isSaving}
        lastSaved={lastSaved}
        results={results}
        settings={settings}
      />

      <main className="main-content">
        <div className="page-container">
          {activeTab === 'oracle' && <Oracle {...sharedProps} />}
          {activeTab === 'data' && <Data {...sharedProps} />}
          {activeTab === 'regional' && <Regional {...sharedProps} />}
          {activeTab === 'history' && <History {...sharedProps} />}
          {activeTab === 'logic' && <ModelLogic />}
          {activeTab === 'users' && <Users addToast={addToast} />}
          {activeTab === 'performance' && <Performance addToast={addToast} />}
          {activeTab === 'settings' && (
            <Settings settings={settings} saveSettings={saveSettings} addToast={addToast} apiStatus={apiStatus} fetchKAPSData={fetchKAPSData} fetchKICPData={fetchKICPData} liveRate={inputs.currency_rate} />
          )}
        </div>
      </main>

      <Toast toasts={toasts} />
    </div>
  );
}
